import type {
  EnvironmentRestriction,
  RestrictionTypeEnum,
} from "~/api/generated/model";
import { imageKey } from "~/components/map/registry/layerToggles";
import { DEFAULT_LAYER_MIN_ZOOM } from "~/components/map/registry/layerZoom";
import type { LayerGroup, MapLayer } from "~/components/map/registry/mapLayer";
import { wfsUrl } from "~/components/map/registry/ogcUrl";

/** A layer id is an API restriction `type`, so a restriction's `type` doubles as its layer id. */
export type MapLayerId = RestrictionTypeEnum;

export const MAP_LAYER_CATEGORIES = {
  PROTECTION_LINES: "protection_lines",
  POLLUTION: "pollution",
  CULTURAL_HERITAGE: "cultural_heritage",
  NATURE_PROTECTION: "nature_protection",
  FLOODING: "flooding",
} as const;

export type MapLayerCategory =
  (typeof MAP_LAYER_CATEGORIES)[keyof typeof MAP_LAYER_CATEGORIES];

export type RestrictionMapLayer = MapLayer & { id: MapLayerId };

type RestrictionLayerDef = MapLayer extends infer L
  ? L extends MapLayer
    ? Omit<L, "id" | "role" | "minZoom" | "nameKey"> & {
        id: MapLayerId;
        category: MapLayerCategory;
      }
    : never
  : never;

const WFS_BASE_AREALEDITERING =
  "https://arealeditering-dist-geo.miljoeportal.dk/geoserver/wfs";

const WFS_BASE_JORD = "https://jord.miljoeportal.dk/geo/wfs";

const WFS_BASE_KULTURARV = "https://www.kulturarv.dk/geoserver/ows";

const WFS_BASE_FF_KULTURARV = "https://www.kulturarv.dk/ffgeoserver/public/wfs";

const WFS_BASE_MILJOEGIS = "https://wfs2-miljoegis.mim.dk/dai/ows";

const WFS_BASE_MILJOEGIS_NATUR = "https://wfs2-miljoegis.mim.dk/natur/ows/wfs";

/**
 * Single pre-rendered XYZ tileset for rain flooding. All return-period bands (2/5/10/20-year) are
 * baked into this one PNG pyramid as distinct colour bands, so every rain_flooding_* layer points
 * at the same URL and they render as one shared raster on the map.
 */
const RAIN_FLOODING_TILES =
  "https://datainsightsdk.blob.core.windows.net/di-flood-rain/{z}/{x}/{y}.png";

/**
 * Single pre-rendered XYZ tileset for ocean flooding. Like the rain tileset, every return-period
 * band (1/5/20-year) is baked into this one PNG pyramid as a distinct colour band, so all
 * ocean_flooding_* layers point at the same URL and render as one shared raster on the map.
 */
const OCEAN_FLOODING_TILES =
  "https://datainsightsdk.blob.core.windows.net/di-flood-ocean/{z}/{x}/{y}.png";

/** Both flooding pyramids are rendered down to zoom 18 only; requesting deeper tiles 404s. */
const FLOODING_TILES_MAX_ZOOM = 18;

// ─── Layer config ─────────────────────────────────────────────────────────────

const LAYER_DEFS: RestrictionLayerDef[] = [
  // Flooding return-period bands share one tileset per hazard; separate entries give the restriction
  // list and the legend one row per band. They come first so they draw beneath the vectors.
  {
    kind: "raster",
    id: "rain_flooding2",
    category: MAP_LAYER_CATEGORIES.FLOODING,
    source: { url: RAIN_FLOODING_TILES },
    maxZoom: FLOODING_TILES_MAX_ZOOM,
    selectionLabelKey: "restrictions:layers.rain_flooding",
    legendColor: "#28338F",
  },
  {
    kind: "raster",
    id: "rain_flooding5",
    category: MAP_LAYER_CATEGORIES.FLOODING,
    source: { url: RAIN_FLOODING_TILES },
    maxZoom: FLOODING_TILES_MAX_ZOOM,
    selectionLabelKey: "restrictions:layers.rain_flooding",
    legendColor: "#385CAE",
  },
  {
    kind: "raster",
    id: "rain_flooding10",
    category: MAP_LAYER_CATEGORIES.FLOODING,
    source: { url: RAIN_FLOODING_TILES },
    maxZoom: FLOODING_TILES_MAX_ZOOM,
    selectionLabelKey: "restrictions:layers.rain_flooding",
    legendColor: "#53A1EF",
  },
  {
    kind: "raster",
    id: "rain_flooding20",
    category: MAP_LAYER_CATEGORIES.FLOODING,
    source: { url: RAIN_FLOODING_TILES },
    maxZoom: FLOODING_TILES_MAX_ZOOM,
    selectionLabelKey: "restrictions:layers.rain_flooding",
    legendColor: "#87F5FF",
  },
  {
    kind: "raster",
    id: "flooding_ocean1",
    category: MAP_LAYER_CATEGORIES.FLOODING,
    source: { url: OCEAN_FLOODING_TILES },
    maxZoom: FLOODING_TILES_MAX_ZOOM,
    selectionLabelKey: "restrictions:layers.flooding_ocean",
    legendColor: "#082E81",
  },
  {
    kind: "raster",
    id: "flooding_ocean5",
    category: MAP_LAYER_CATEGORIES.FLOODING,
    source: { url: OCEAN_FLOODING_TILES },
    maxZoom: FLOODING_TILES_MAX_ZOOM,
    selectionLabelKey: "restrictions:layers.flooding_ocean",
    legendColor: "#6991E9",
  },
  {
    kind: "raster",
    id: "flooding_ocean20",
    category: MAP_LAYER_CATEGORIES.FLOODING,
    source: { url: OCEAN_FLOODING_TILES },
    maxZoom: FLOODING_TILES_MAX_ZOOM,
    selectionLabelKey: "restrictions:layers.flooding_ocean",
    legendColor: "#DEE6F8",
  },
  {
    id: "skovbyggelinjer",
    category: MAP_LAYER_CATEGORIES.PROTECTION_LINES,
    kind: "wfs",
    source: { url: wfsUrl(WFS_BASE_AREALEDITERING, "dai:skovbyggelinjer") },
    style: {
      fillColor: "#16a34a",
      fillOpacity: 0.2,
      lineColor: "#16a34a",
      lineWidth: 2,
    },
    intersectionStyle: {
      fillColor: "#16a34a",
      fillOpacity: 0.15,
      pattern: "hatch",
      patternColor: "#16a34a",
    },
  },
  {
    id: "kirkebyggelinjer",
    category: MAP_LAYER_CATEGORIES.PROTECTION_LINES,
    kind: "wfs",
    source: { url: wfsUrl(WFS_BASE_AREALEDITERING, "dai:kirkebyggelinjer") },
    style: {
      fillColor: "#7c3aed",
      fillOpacity: 0.2,
      lineColor: "#7c3aed",
      lineWidth: 2,
    },
    intersectionStyle: {
      fillColor: "#7c3aed",
      fillOpacity: 0.15,
      pattern: "crosshatch",
      patternColor: "#7c3aed",
    },
  },
  {
    id: "bes_vandloeb",
    category: MAP_LAYER_CATEGORIES.NATURE_PROTECTION,
    kind: "wfs",
    geometryType: "line",
    source: { url: wfsUrl(WFS_BASE_AREALEDITERING, "dai:bes_vandloeb") },
    style: {
      lineColor: "#2563eb",
      lineWidth: 2,
    },
  },
  {
    id: "fredede_omr",
    category: MAP_LAYER_CATEGORIES.NATURE_PROTECTION,
    kind: "wfs",
    source: { url: wfsUrl(WFS_BASE_AREALEDITERING, "dai:fredede_omr") },
    style: {
      fillColor: "#dc2626",
      fillOpacity: 0.2,
      lineColor: "#dc2626",
      lineWidth: 2,
    },
    intersectionStyle: {
      fillColor: "#dc2626",
      fillOpacity: 0.15,
      pattern: "hatch",
      patternColor: "#dc2626",
    },
  },
  {
    id: "jordforurening_v1",
    category: MAP_LAYER_CATEGORIES.POLLUTION,
    kind: "wfs",
    source: { url: wfsUrl(WFS_BASE_JORD, "DKJord:View_V1Flader") },
    style: {
      fillColor: "#f59e0b",
      fillOpacity: 0.2,
      lineColor: "#f59e0b",
      lineWidth: 2,
    },
    intersectionStyle: {
      fillColor: "#f59e0b",
      fillOpacity: 0.15,
      pattern: "hatch",
      patternColor: "#f59e0b",
    },
  },
  {
    id: "jordforurening_v2",
    category: MAP_LAYER_CATEGORIES.POLLUTION,
    kind: "wfs",
    source: { url: wfsUrl(WFS_BASE_JORD, "DKJord:View_V2Flader") },
    style: {
      fillColor: "#ef4444",
      fillOpacity: 0.2,
      lineColor: "#ef4444",
      lineWidth: 2,
    },
    intersectionStyle: {
      fillColor: "#ef4444",
      fillOpacity: 0.15,
      pattern: "crosshatch",
      patternColor: "#ef4444",
    },
  },
  {
    id: "jordforurening_nuanceret",
    category: MAP_LAYER_CATEGORIES.POLLUTION,
    kind: "wfs",
    source: { url: wfsUrl(WFS_BASE_JORD, "DKJord:View_NuanceringsFlader") },
    style: {
      fillColor: "#db2777",
      fillOpacity: 0.2,
      lineColor: "#db2777",
      lineWidth: 2,
    },
    intersectionStyle: {
      fillColor: "#db2777",
      fillOpacity: 0.15,
      pattern: "dots",
      patternColor: "#db2777",
    },
  },
  {
    id: "soe_beskyttelse",
    category: MAP_LAYER_CATEGORIES.PROTECTION_LINES,
    kind: "wfs",
    source: { url: wfsUrl(WFS_BASE_AREALEDITERING, "dai:soe_bes_linjer") },
    style: {
      fillColor: "#0ea5e9",
      fillOpacity: 0.2,
      lineColor: "#0ea5e9",
      lineWidth: 2,
    },
    intersectionStyle: {
      fillColor: "#0ea5e9",
      fillOpacity: 0.15,
      pattern: "dots",
      patternColor: "#0ea5e9",
    },
  },
  {
    id: "bygninger",
    category: MAP_LAYER_CATEGORIES.CULTURAL_HERITAGE,
    kind: "wfs",
    source: { url: wfsUrl(WFS_BASE_KULTURARV, "view_bygning_fredede") },
    geometryType: "point",
    filter: ["==", ["get", "fredet"], true],
    style: {
      fillColor: "#4f46e5",
      fillOpacity: 0.8,
      lineColor: "#312e81",
      lineWidth: 1.5,
      circleRadius: 8,
    },
  },
  {
    id: "fundogfortidsminder_areal_fredet",
    category: MAP_LAYER_CATEGORIES.CULTURAL_HERITAGE,
    kind: "wfs",
    source: {
      url: wfsUrl(
        WFS_BASE_FF_KULTURARV,
        "public:fundogfortidsminder_areal_fredet",
      ),
    },
    style: {
      fillColor: "#a21caf",
      fillOpacity: 0.2,
      lineColor: "#a21caf",
      lineWidth: 2,
    },
    intersectionStyle: {
      fillColor: "#a21caf",
      fillOpacity: 0.15,
      pattern: "hatch",
      patternColor: "#a21caf",
    },
  },
  {
    id: "strandbeskyttelse",
    category: MAP_LAYER_CATEGORIES.PROTECTION_LINES,
    kind: "wfs",
    source: {
      proxy: "property-map",
      layer: "restriction.strandbeskyttelse",
    },
    style: {
      fillColor: "#ea580c",
      fillOpacity: 0.2,
      lineColor: "#ea580c",
      lineWidth: 2,
    },
    intersectionStyle: {
      fillColor: "#ea580c",
      fillOpacity: 0.15,
      pattern: "dots",
      patternColor: "#ea580c",
    },
  },
  {
    id: "klitfredning",
    category: MAP_LAYER_CATEGORIES.NATURE_PROTECTION,
    kind: "wfs",
    source: { proxy: "property-map", layer: "restriction.klitfredning" },
    style: {
      fillColor: "#eab308",
      fillOpacity: 0.2,
      lineColor: "#eab308",
      lineWidth: 2,
    },
    intersectionStyle: {
      fillColor: "#eab308",
      fillOpacity: 0.15,
      pattern: "hatch",
      patternColor: "#eab308",
    },
  },
  {
    id: "fundogfortidsminder_areal_beskyttelse",
    category: MAP_LAYER_CATEGORIES.PROTECTION_LINES,
    kind: "wfs",
    source: {
      url: wfsUrl(
        WFS_BASE_FF_KULTURARV,
        "public:fundogfortidsminder_areal_beskyttelse",
      ),
    },
    style: {
      fillColor: "#b91c1c",
      fillOpacity: 0.2,
      lineColor: "#b91c1c",
      lineWidth: 1.5,
      lineDashArray: [4, 3],
    },
    intersectionStyle: {
      fillColor: "#b91c1c",
      fillOpacity: 0.15,
      pattern: "crosshatch",
      patternColor: "#b91c1c",
    },
  },
  {
    id: "bes_naturtyper",
    category: MAP_LAYER_CATEGORIES.NATURE_PROTECTION,
    kind: "wfs",
    source: { url: wfsUrl(WFS_BASE_AREALEDITERING, "dai:bes_naturtyper") },
    style: {
      fillColor: "#166534",
      fillOpacity: 0.2,
      lineColor: "#166534",
      lineWidth: 1.5,
    },
    intersectionStyle: {
      fillColor: "#166534",
      fillOpacity: 0.15,
      pattern: "hatch",
      patternColor: "#166534",
    },
  },
  {
    id: "egekrat",
    category: MAP_LAYER_CATEGORIES.NATURE_PROTECTION,
    kind: "wfs",
    source: { url: wfsUrl(WFS_BASE_MILJOEGIS, "dai:egekrat") },
    style: {
      fillColor: "#78350f",
      fillOpacity: 0.2,
      lineColor: "#78350f",
      lineWidth: 1.5,
    },
    intersectionStyle: {
      fillColor: "#78350f",
      fillOpacity: 0.15,
      pattern: "dots",
      patternColor: "#78350f",
    },
  },
  {
    id: "natur_vildt_reservat",
    category: MAP_LAYER_CATEGORIES.NATURE_PROTECTION,
    kind: "wfs",
    source: {
      url: wfsUrl(WFS_BASE_AREALEDITERING, "dai:natur_vildt_reservat"),
    },
    style: {
      fillColor: "#10b981",
      fillOpacity: 0.2,
      lineColor: "#10b981",
      lineWidth: 1.5,
    },
    intersectionStyle: {
      fillColor: "#10b981",
      fillOpacity: 0.15,
      pattern: "crosshatch",
      patternColor: "#10b981",
    },
  },
  {
    id: "Natura_2000",
    category: MAP_LAYER_CATEGORIES.NATURE_PROTECTION,
    kind: "wfs",
    source: { url: wfsUrl(WFS_BASE_MILJOEGIS_NATUR, "natura_2000_omraader") },
    style: {
      fillColor: "#0d9488",
      fillOpacity: 0.2,
      lineColor: "#0d9488",
      lineWidth: 2,
    },
    intersectionStyle: {
      fillColor: "#0d9488",
      fillOpacity: 0.15,
      pattern: "waves",
      patternColor: "#0d9488",
    },
  },
  {
    id: "FredskovFlade",
    category: MAP_LAYER_CATEGORIES.NATURE_PROTECTION,
    kind: "wfs",
    source: { proxy: "property-map", layer: "restriction.fredskov" },
    style: {
      fillColor: "#4d7c0f",
      fillOpacity: 0.2,
      lineColor: "#4d7c0f",
      lineWidth: 2,
    },
    intersectionStyle: {
      fillColor: "#4d7c0f",
      fillOpacity: 0.15,
      pattern: "crosshatch",
      patternColor: "#4d7c0f",
    },
  },
  {
    id: "vandloeb_beskyt",
    category: MAP_LAYER_CATEGORIES.PROTECTION_LINES,
    kind: "wfs",
    source: { url: wfsUrl(WFS_BASE_AREALEDITERING, "aa_bes_linjer") },
    style: {
      fillColor: "#0891b2",
      fillOpacity: 0.2,
      lineColor: "#0891b2",
      lineWidth: 2,
    },
    intersectionStyle: {
      fillColor: "#0891b2",
      fillOpacity: 0.15,
      pattern: "dots",
      patternColor: "#0891b2",
    },
  },
];

const RESTRICTION_LAYERS: RestrictionMapLayer[] = LAYER_DEFS.map(
  ({ category: _category, ...def }): RestrictionMapLayer => ({
    ...def,
    role: "overlay",
    minZoom: DEFAULT_LAYER_MIN_ZOOM,
    nameKey: `restrictions:layers.${def.id}`,
  }),
);

export type RestrictionLayerGroup = LayerGroup & {
  id: MapLayerCategory;
  layerIds: MapLayerId[];
};

export const RESTRICTION_LAYER_REGISTRY = {
  layers: RESTRICTION_LAYERS,
  groups: Object.values(MAP_LAYER_CATEGORIES).map(
    (category): RestrictionLayerGroup => ({
      id: category,
      labelKey: `restrictions:categories.${category}`,
      layerIds: LAYER_DEFS.filter((d) => d.category === category).map(
        (d) => d.id,
      ),
    }),
  ),
};

/**
 * Starts with every layer that restricts the property switched on. A flooding band's legend only
 * reads as a gradient with its sibling bands, so one restricting band switches on its whole image.
 */
export function initialEnabledLayerIds(
  restrictions: EnvironmentRestriction[],
): string[] {
  const restrictedIds = new Set<string>(restrictions.map((r) => r.type));
  const restrictedImages = new Set(
    RESTRICTION_LAYERS.filter((l) => restrictedIds.has(l.id)).map(imageKey),
  );
  return RESTRICTION_LAYERS.filter((l) =>
    restrictedImages.has(imageKey(l)),
  ).map((l) => l.id);
}
